import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { useNavigate } from 'react-router-dom';

export function ErrorFallback({ error, resetErrorBoundary }) {
    const navigate = useNavigate();

    const handleGoHome = () => {
        resetErrorBoundary();
        navigate('/dashboards');
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(160deg, #0a1f38 0%, #0f2942 55%, #0d2236 100%)',
            backgroundImage: `
        linear-gradient(160deg, #0a1f38 0%, #0f2942 55%, #0d2236 100%),
        linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
      `,
            backgroundSize: 'cover, 40px 40px, 40px 40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
        }}>
            <Box sx={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                p: { xs: '32px 24px', sm: '48px 40px 40px' },
                maxWidth: 540,
                width: '100%',
                textAlign: 'center',
            }}>

                {/* Icon */}
                <Box sx={{
                    width: 72, height: 72,
                    borderRadius: '50%',
                    background: 'rgba(220,60,50,0.12)',
                    border: '1px solid rgba(220,60,50,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3.5,
                }}>
                    <WarningAmberRoundedIcon sx={{ fontSize: 28, color: '#e05a4e' }} />
                </Box>

                {/* Badge */}
                <Box component="span" sx={{
                    display: 'inline-block',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    letterSpacing: '0.08em',
                    color: 'rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    px: 1.25, py: 0.5,
                    borderRadius: '4px',
                    mb: 2.5,
                    textTransform: 'uppercase',
                }}>
                    Runtime Error
                </Box>

                <Typography variant="h5" sx={{
                    color: '#fff',
                    fontWeight: 600,
                    letterSpacing: '-0.3px',
                    mb: 1.25,
                }}>
                    Something went wrong
                </Typography>

                <Typography sx={{
                    fontSize: '14px',
                    fontWeight: 300,
                    color: 'rgba(255,255,255,0.45)',
                    lineHeight: 1.7,
                    mb: 3.5,
                }}>
                    An unexpected error occurred while loading this page.
                    <br />Your session and data are safe.
                </Typography>

                {/* Error box */}
                {error?.message && (
                    <Box sx={{
                        background: 'rgba(0,0,0,0.25)',
                        border: '1px solid rgba(220,60,50,0.2)',
                        borderLeft: '3px solid #e05a4e',
                        borderRadius: '8px',
                        p: '14px 16px',
                        mb: 4,
                        textAlign: 'left',
                    }}>
                        <Typography sx={{
                            fontFamily: 'monospace',
                            fontSize: '10px',
                            letterSpacing: '0.1em',
                            color: '#e05a4e',
                            textTransform: 'uppercase',
                            mb: 0.75,
                        }}>
                            Error details
                        </Typography>
                        <Typography sx={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            color: 'rgba(255,255,255,0.55)',
                            wordBreak: 'break-word',
                            lineHeight: 1.6,
                        }}>
                            {error.message}
                        </Typography>
                    </Box>
                )}

                {/* Buttons */}
                <Stack direction="row" spacing={1.5} justifyContent="center">
                    <Button
                        onClick={resetErrorBoundary}
                        startIcon={<RefreshRoundedIcon />}
                        sx={{
                            background: '#1a4a78',
                            border: '1px solid rgba(255,255,255,0.15)',
                            color: '#fff',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 400,
                            px: 2.5, py: 1.1,
                            '&:hover': { background: '#215a91' },
                        }}
                    >
                        Try again
                    </Button>
                    <Button
                        onClick={handleGoHome}
                        startIcon={<HomeRoundedIcon />}
                        sx={{
                            background: 'transparent',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.45)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 400,
                            px: 2.5, py: 1.1,
                            '&:hover': {
                                background: 'transparent',
                                border: '1px solid rgba(255,255,255,0.2)',
                                color: 'rgba(255,255,255,0.7)',
                            },
                        }}
                    >
                        Go to dashboard
                    </Button>
                </Stack>

                <Typography sx={{
                    mt: 3.5,
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.2)',
                }}>
                    If this keeps happening, contact your administrator
                </Typography>
            </Box>
        </Box>
    );
}